import React from 'react';
import copy from 'copy-to-clipboard';
import { format } from 'date-fns';
import { upperFirst } from 'lodash';
import { ArrowTopRightOnSquareIcon, FingerPrintIcon } from '@heroicons/react/24/outline';

import Dash from '@dashboard/components/Dash';
import Link from '@dashboard/components/Link';
import { CopyButton } from '@dashboard/components/OrderPage';
import Tooltip from '@dashboard/components/Tooltip';
import { Badge } from '@dashboard/components/ui/badge';
import { Button } from '@dashboard/components/ui/button';
import { Checkbox } from '@dashboard/components/ui/checkbox';
import { TableCell, TableRow } from '@dashboard/components/ui/table';
import { toast } from '@dashboard/context/ToastContext';
import { useUpdateTeamMemberEmailSettings } from '@dashboard/hooks/queries/useAdminMutations';

const CRISP_CONTACTS_URL =
  'https://app.crisp.chat/website/abe8e9e1-02b6-4715-b289-7573fd280b36/contacts';

/**
 * Payment and submission notifications are one control because a member who
 * asks to stop being emailed wants both off. Defaults mirror the User model, so
 * a member whose document predates a field shows the state they are really in.
 */
export const EMAIL_SETTINGS = [
  {
    key: 'marketing',
    label: 'Marketing',
    fields: ['marketingConsent'],
    defaultValue: false,
  },
  {
    key: 'notifications',
    label: 'Notifications',
    fields: ['paymentNotifications', 'submissionNotifications'],
    defaultValue: true,
  },
];

/**
 * A grouped control is indeterminate when its fields disagree, so an admin can
 * see the split rather than being shown a state the member is not in.
 */
const getCheckedState = (user, { fields, defaultValue }) => {
  const values = fields.map(field => user[field] ?? defaultValue);

  if (values.every(Boolean)) return true;
  if (values.some(Boolean)) return 'indeterminate';

  return false;
};

export default function TeamMemberRow({ teamMember, sellerId }) {
  const { user, role, otherStores = [] } = teamMember;

  const updateEmailSettings = useUpdateTeamMemberEmailSettings({
    onError: error => {
      toast({
        title: 'Error updating email settings',
        description: error?.message,
        type: 'error',
      });
    },
  });

  const handleToggle = (fields, label) => checked => {
    const turnedOn = checked === true;
    const settings = Object.fromEntries(fields.map(field => [field, turnedOn]));

    updateEmailSettings.mutate(
      { sellerId, userId: user._id, settings },
      {
        onSuccess: () => {
          toast({
            title: `${label} ${turnedOn ? 'turned on' : 'turned off'}`,
            description: user.email,
            type: 'success',
          });
        },
      },
    );
  };

  return (
    <TableRow className="hover:bg-transparent">
      <TableCell className="pl-4 align-top whitespace-nowrap">
        <div className="group flex w-fit items-center gap-x-1 font-medium text-gray-900 dark:text-foreground">
          {user.name || <Dash />}
          {user.name ? <CopyButton text={user.name} /> : null}
        </div>
        <div className="group flex w-fit items-center gap-x-1 text-gray-500 dark:text-muted-foreground">
          {user.email}
          <CopyButton text={user.email} />
        </div>
      </TableCell>
      <TableCell className="align-top">{upperFirst(role)}</TableCell>
      <TableCell className="w-full align-top whitespace-normal">
        {otherStores.length ? (
          <div className="flex flex-wrap gap-1">
            {otherStores.map(store => (
              <Badge key={store._id} variant="outline">
                <Link to={`/admin/merchants/${store._id}`}>
                  {store.displayName || store.name} - {store.role}
                </Link>
              </Badge>
            ))}
          </div>
        ) : (
          <span className="text-gray-500 dark:text-muted-foreground">None</span>
        )}
      </TableCell>
      <TableCell className="align-top whitespace-nowrap">
        <div className="space-y-1.5">
          {EMAIL_SETTINGS.map(setting => {
            const id = `${user._id}-${setting.key}`;

            return (
              <div key={setting.key} className="flex gap-x-2 items-center">
                <Checkbox
                  id={id}
                  checked={getCheckedState(user, setting)}
                  onCheckedChange={handleToggle(setting.fields, setting.label)}
                  disabled={updateEmailSettings.isPending}
                />
                <label htmlFor={id} className="whitespace-nowrap">
                  {setting.label}
                </label>
              </div>
            );
          })}
        </div>
      </TableCell>
      <TableCell className="align-top whitespace-nowrap">
        <div>
          {user.lastActivity ? format(new Date(user.lastActivity), 'd MMM, yyyy, HH:mm') : <Dash />}
        </div>
        {user.createdAt ? (
          <div className="text-gray-500 dark:text-muted-foreground">
            Joined {format(new Date(user.createdAt), 'd MMM, yyyy')}
          </div>
        ) : null}
      </TableCell>
      <TableCell className="pr-4 align-top">
        <div className="flex items-center justify-end gap-x-2">
          <Button asChild variant="outline" size="sm">
            <a
              href={`${CRISP_CONTACTS_URL}?query=${encodeURIComponent(user.email)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in Crisp
              <ArrowTopRightOnSquareIcon />
            </a>
          </Button>
          <Tooltip label="Copy user ID" asChild>
            <Button
              variant="outline"
              size="icon-sm"
              aria-label="Copy user ID"
              onClick={() => {
                copy(user._id);
                toast({ title: 'User ID copied', type: 'success' });
              }}
            >
              <FingerPrintIcon />
            </Button>
          </Tooltip>
        </div>
      </TableCell>
    </TableRow>
  );
}
